var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import CargomaticAlert from 'components/ui/Alert';
import Badge from 'components/v2/Badge';
import Button from 'components/v2/buttons/Button';
import Checkbox from 'components/v2/forms/Checkbox';
import CollapsibleItem from 'components/v2/list/CollapsibleItem';
import LayoutWithSidebar from 'components/v2/LayoutWithSidebar';
import Panel from 'components/v2/Panel';
import PanelSection from 'components/v2/PanelSection';
import ToggleSwitch from 'components/v2/ToggleSwitch';
import CargomaticModal from 'components/v2/modal';
import formatMoney from 'utils/formatters/Money';
import { sluggify } from 'utils/localization/utils';
import { cloneDeep, uniqBy, groupBy } from 'lodash';
import { generateInvoicesByGrouping, validateInvoiceChargesByGrouping } from 'utils/AccountingWebAPIUtils';
import { showToast } from 'stores/ToastStore';
import Label from 'components/v2/forms/Label';
import { INVOICE_GROUPING_TYPES } from 'constants/InvoiceGroupingTypes';
import LoadingCircle from 'components/ui/LoadingCircle';
import SessionStore from 'stores/SessionStore';
const FUEL_SURCHARGE_TYPES = ['fuel_surcharge', 'fuel_surcharge_bobtail', 'fuel_surcharge_linehaul'];
const INVOICE_GROUPINGS = {
    ONE_PER_SHIPMENT: {
        ALL: {
            charges: [],
            groupedChargeTypes: []
        },
    },
    ONE_PER_SHIPMENT_PER_CHARGE_TYPE: {
        SHIPPER_COST: {
            charges: [],
            groupedChargeTypes: []
        },
        FUEL_SURCHARGE: {
            charges: [],
            groupedChargeTypes: []
        },
        ALL_BUT_LINE_HAUL_AND_FUEL_SURCHARGE: {
            charges: [],
            groupedChargeTypes: []
        },
    },
    TWO_PER_SHIPMENT: {
        CHASSIS_AND_PER_DIEM: {
            charges: [],
            groupedChargeTypes: []
        },
        ALL_BUT_CHASSIS_AND_PER_DIEM: {
            charges: [],
            groupedChargeTypes: []
        },
    },
    UNLIMITED: {
        SHIPPER_COST: {
            charges: [],
            groupedChargeTypes: []
        },
        FUEL_SURCHARGE: {
            charges: [],
            groupedChargeTypes: []
        },
        ALL_BUT_LINE_HAUL_AND_FUEL_SURCHARGE: {
            charges: [],
            groupedChargeTypes: []
        },
    },
};
export default function InvoiceGroupingModal({ show, orderId, equipment, shipper, carrier, shipments, ledgerType, onClose, t, useShipmentAddress, invoiceSettings }) {
    const [hasRetrievedSettings, setHasRetrievedSettings] = useState(false);
    const [invoiceGrouping, setInvoiceGrouping] = useState('UNLIMITED');
    const [groupedCharges, setGroupedCharges] = useState([]);
    const [groupAll, setGroupAll] = useState(false);
    const [invoiceSuccesses, setInvoiceSuccesses] = useState([]);
    const [invoiceErrors, setInvoiceErrors] = useState([]);
    const [lockInvoicing, setLockInvoicing] = useState(false);
    const [isValidatingGroupedCharges, setIsValidatingGroupedCharges] = useState(false);
    const [areValidGroupings, setAreValidGroupings] = useState(false);
    const [validCharges, setValidCharges] = useState([]);
    const [invalidCharges, setInvalidCharges] = useState([]);
    const [showValidationAlert, setShowValidationAlert] = useState(true);
    const getInvoiceSettings = () => {
        if (invoiceSettings === null || invoiceSettings === void 0 ? void 0 : invoiceSettings.invoice_grouping)
            setInvoiceGrouping(invoiceSettings === null || invoiceSettings === void 0 ? void 0 : invoiceSettings.invoice_grouping);
        setHasRetrievedSettings(true);
    };
    useEffect(() => {
        getInvoiceSettings();
        if (shipper === null || shipper === void 0 ? void 0 : shipper.group_like_invoice_items)
            setGroupAll(true);
    }, [shipper]);
    useEffect(() => {
        validateSelectedGroupedCharges();
    }, [groupedCharges, invoiceGrouping]);
    useEffect(() => {
        if (hasRetrievedSettings) {
            let reducedShipments = shipments.reduce((acc, shipment) => {
                acc.push(sortCharges(invoiceGrouping, shipment));
                return acc;
            }, []);
            setGroupedCharges(reducedShipments);
        }
    }, [hasRetrievedSettings, groupAll]);
    const isInvoiceButtonDisabled = () => {
        const canInvoice = (!lockInvoicing &&
            !isValidatingGroupedCharges &&
            areValidGroupings);
        return !canInvoice;
    };
    const noDeliveryInvoiceButton = () => {
        if (!SessionStore.isBillingManagerAdmin()) {
            return null;
        }
        return (React.createElement(Button, { color: "green", className: "mr-2", onClick: () => generateInvoices({ noDeliveryInvoice: true }), disabled: isInvoiceButtonDisabled() }, t('GENERATE_NO_DELIVERY_INVOICE')));
    };
    const generateInvoices = async (options) => {
        var _a, _b, _c, _d;
        try {
            setLockInvoicing(true);
            const { noDeliveryInvoice = false } = options;
            const data = mapDataPayload(groupedCharges);
            const selectedCharges = data.map((charge) => {
                let _a = charge.grouping, { SHIPPER_COST } = _a, remainder = __rest(_a, ["SHIPPER_COST"]);
                charge.grouping = Object.assign(Object.assign({}, remainder), { LINE_HAUL: SHIPPER_COST });
                return charge;
            });
            const payload = {
                ledgerDate: moment.tz(DEFAULT_TIMEZONE).toISOString(),
                selectedCharges,
                useShipmentAddress,
                noDeliveryInvoice,
            };
            const response = await generateInvoicesByGrouping(orderId, payload);
            setLockInvoicing(false);
            const successes = ((_b = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.results) === null || _b === void 0 ? void 0 : _b.successes) || [];
            const errors = ((_d = (_c = response === null || response === void 0 ? void 0 : response.data) === null || _c === void 0 ? void 0 : _c.results) === null || _d === void 0 ? void 0 : _d.errors) || [];
            setInvoiceSuccesses(successes);
            setInvoiceErrors(errors);
            if (!errors.length && successes.length) {
                showToast(t('INVOICE_GENERATION_SUCCESS'));
                onClose();
                return;
            }
            if (errors.length && successes.length) {
                showToast(t('INVOICE_GENERATE_MIXED_SUCCESS'));
                return;
            }
            showToast(t('INVOICE_GENERATION_FAILURE', null, 'error'));
        }
        catch (e) {
            console.error(`Error generating invoice: ${e}`);
            showToast(t('INVOICE_GENERATION_FAILURE'), null, 'error');
            setLockInvoicing(false);
        }
    };
    const getShipmentGroupCharges = (shipmentId, group) => {
        const shipmentGroup = groupedCharges.find((shipmentGrouping) => (shipmentGrouping === null || shipmentGrouping === void 0 ? void 0 : shipmentGrouping.shipmentId) === shipmentId);
        if (!shipmentGroup) {
            return [];
        }
        const chargeGroup = (shipmentGroup === null || shipmentGroup === void 0 ? void 0 : shipmentGroup.grouping[group]) || {};
        const charges = chargeGroup === null || chargeGroup === void 0 ? void 0 : chargeGroup.charges;
        if (!(charges === null || charges === void 0 ? void 0 : charges.length)) {
            return [];
        }
        return charges;
    };
    const getGroupedCharges = (shipmentId, group, chargeId) => {
        var _a;
        const charges = getShipmentGroupCharges(shipmentId, group);
        const groupedType = (_a = charges.find((charge) => charge._id === chargeId)) === null || _a === void 0 ? void 0 : _a.type;
        if (!groupedType) {
            return [];
        }
        const chargeTypeGroup = charges.filter((charge) => (charge === null || charge === void 0 ? void 0 : charge.type) === groupedType);
        return chargeTypeGroup.map((charge) => charge._id);
    };
    const isGroupEligible = (shipmentId, group, type) => {
        const charges = getShipmentGroupCharges(shipmentId, group);
        return charges.filter((charge) => (charge === null || charge === void 0 ? void 0 : charge.type) === type).length > 1;
    };
    const updateSelectedCharges = (shipmentId, group, chargeIds, selected) => {
        setGroupedCharges((currentGroupState) => {
            const currentShipmentGrouping = currentGroupState.find((row) => row.shipmentId === shipmentId);
            if (!currentShipmentGrouping) {
                return currentGroupState;
            }
            const currentShipmentChargeState = (currentShipmentGrouping === null || currentShipmentGrouping === void 0 ? void 0 : currentShipmentGrouping.grouping[group]) || {};
            const shipmentGroupings = currentGroupState.filter((current) => current.shipmentId !== shipmentId);
            const charges = currentShipmentChargeState.charges.map((charge) => {
                if (chargeIds.includes(charge._id)) {
                    return mapSelectedCharge(charge, selected);
                }
                return charge;
            });
            const updatedShipmentGrouping = Object.assign(Object.assign({}, currentShipmentGrouping), { grouping: Object.assign(Object.assign({}, currentShipmentGrouping.grouping), { [group]: Object.assign(Object.assign({}, currentShipmentChargeState), { charges }) }) });
            return [...shipmentGroupings, updatedShipmentGrouping];
        });
    };
    const updateGroupSelectedCharges = (shipmentId, group, chargesSelected) => {
        const shipmentGroup = groupedCharges.find((group) => group.shipmentId === shipmentId);
        const shipmentGroupings = (shipmentGroup === null || shipmentGroup === void 0 ? void 0 : shipmentGroup.grouping) || {};
        const shipmentGroupCharges = shipmentGroupings[group] || {};
        const shipmentChargeIds = shipmentGroupCharges === null || shipmentGroupCharges === void 0 ? void 0 : shipmentGroupCharges.charges.map((charge) => charge._id);
        updateSelectedCharges(shipmentId, group, shipmentChargeIds, chargesSelected);
    };
    const groupCharge = (shipmentId, group, chargeType) => {
        setGroupedCharges((currentGroupState) => {
            const currentShipmentGrouping = currentGroupState.find((row) => row.shipmentId === shipmentId);
            if (!currentShipmentGrouping) {
                return currentGroupState;
            }
            const currentShipmentChargeState = (currentShipmentGrouping === null || currentShipmentGrouping === void 0 ? void 0 : currentShipmentGrouping.grouping[group]) || {};
            let groupedChargeTypes = [];
            if (((currentShipmentChargeState === null || currentShipmentChargeState === void 0 ? void 0 : currentShipmentChargeState.groupedChargeTypes) || []).includes(chargeType)) {
                groupedChargeTypes = currentShipmentChargeState.groupedChargeTypes.filter((type) => type !== chargeType);
            }
            else {
                groupedChargeTypes = currentShipmentChargeState.groupedChargeTypes.concat(chargeType);
            }
            if (groupedChargeTypes.some((charge) => charge.selected)) {
                groupedChargeTypes = groupedChargeTypes.map(mapSelectedCharge(charge, true));
            }
            const shipmentGroupings = currentGroupState.filter((current) => current.shipmentId !== shipmentId);
            const updatedShipmentGrouping = Object.assign(Object.assign({}, currentShipmentGrouping), { grouping: Object.assign(Object.assign({}, currentShipmentGrouping.grouping), { [group]: Object.assign(Object.assign({}, currentShipmentChargeState), { groupedChargeTypes }) }) });
            const sortedGroupings = [...shipmentGroupings, updatedShipmentGrouping];
            return sortedGroupings;
        });
    };
    const mapSelectedCharge = (charge, isSelected) => {
        return Object.assign(Object.assign({}, charge), { selected: isSelected });
    };
    const sortCharges = (grouping, shipment) => {
        let sortedCharges = cloneDeep(INVOICE_GROUPINGS[grouping]);
        const charges = (shipment[ledgerType] || []).filter((charge) => !charge.processed);
        if (groupAll) {
            const chargeTypes = charges.reduce((acc, charge) => {
                if (!acc.includes(charge === null || charge === void 0 ? void 0 : charge.type)) {
                    acc.push(charge === null || charge === void 0 ? void 0 : charge.type);
                }
                return acc;
            }, []);
            Object.keys(sortedCharges).forEach((group) => {
                sortedCharges[group].groupedChargeTypes = chargeTypes;
            });
        }
        if (grouping === INVOICE_GROUPING_TYPES.TWO_PER_SHIPMENT) {
            const chassisAndPerDiem = charges.filter(charge => charge.type === "chassis" || charge.type === "per_diem").map(charge => mapSelectedCharge(charge, true));
            const allButChassisAndPerDiem = charges.filter(charge => charge.type !== "chassis" && charge.type !== "per_diem").map(charge => mapSelectedCharge(charge, true));
            sortedCharges.CHASSIS_AND_PER_DIEM.charges = chassisAndPerDiem;
            sortedCharges.ALL_BUT_CHASSIS_AND_PER_DIEM.charges = allButChassisAndPerDiem;
        }
        else if (grouping === INVOICE_GROUPING_TYPES.ONE_PER_SHIPMENT) {
            charges.forEach((charge) => {
                sortedCharges.ALL.charges.push(mapSelectedCharge(charge, true));
            });
        }
        else {
            charges.forEach((charge) => {
                const mappedCharge = mapSelectedCharge(charge, true);
                if (charge.type === "shipper_cost") {
                    sortedCharges.SHIPPER_COST.charges.push(mappedCharge);
                }
                else if (FUEL_SURCHARGE_TYPES.includes(charge.type)) {
                    sortedCharges.FUEL_SURCHARGE.charges.push(mappedCharge);
                }
                else {
                    sortedCharges.ALL_BUT_LINE_HAUL_AND_FUEL_SURCHARGE.charges.push(mappedCharge);
                }
            });
        }
        const shipmentGroups = {
            shipmentId: shipment._id,
            grouping: sortedCharges,
        };
        return shipmentGroups;
    };
    const validateSelectedGroupedCharges = async () => {
        setIsValidatingGroupedCharges(true);
        try {
            const data = mapDataPayload(groupedCharges);
            const selectedCharges = data.map((charge) => {
                let _a = charge.grouping, { SHIPPER_COST } = _a, remainder = __rest(_a, ["SHIPPER_COST"]);
                charge.grouping = Object.assign(Object.assign({}, remainder), { LINE_HAUL: SHIPPER_COST });
                return charge;
            });
            if (!(selectedCharges === null || selectedCharges === void 0 ? void 0 : selectedCharges.length)) {
                setIsValidatingGroupedCharges(false);
                return;
            }
            const payload = {
                selectedCharges,
            };
            const response = await validateInvoiceChargesByGrouping(orderId, payload);
            handleChargeValidationResponse(response);
            setIsValidatingGroupedCharges(false);
        }
        catch (e) {
            console.error('Error validating grouped charges: ', e.message);
            showToast(t('ERROR_VALIDATING_GROUPED_CHARGES'));
            setIsValidatingGroupedCharges(false);
        }
    };
    /*
      Take response and for each error, add it to state
    */
    const handleChargeValidationResponse = (response) => {
        try {
            const { data: { results: { valid, validatedChargeGroupingsToInvoice, invalidChargeGroupingsToInvoice, } } } = response;
            if (!showValidationAlert && valid !== areValidGroupings) {
                setShowValidationAlert(true);
            }
            ;
            if (valid) {
                setAreValidGroupings(valid);
                setValidCharges(validatedChargeGroupingsToInvoice);
            }
            else {
                setAreValidGroupings(valid);
                setInvalidCharges(invalidChargeGroupingsToInvoice);
            }
        }
        catch (e) {
            setAreValidGroupings(false);
            throw e;
        }
    };
    const ChargeHeaders = (shipmentId, group) => {
        const shipmentGrouping = (groupedCharges || []).find((group) => group.shipmentId === shipmentId) || {};
        const shipmentCharges = (shipmentGrouping === null || shipmentGrouping === void 0 ? void 0 : shipmentGrouping.grouping[group]) || {};
        const allChargesSelected = ((shipmentCharges === null || shipmentCharges === void 0 ? void 0 : shipmentCharges.charges) || []).every((charge) => charge === null || charge === void 0 ? void 0 : charge.selected);
        return (React.createElement("thead", null,
            React.createElement("tr", null,
                React.createElement("th", { className: "w-4" },
                    React.createElement(Checkbox, { checked: allChargesSelected, onChange: (checked) => updateGroupSelectedCharges(shipmentId, group, checked) })),
                React.createElement("th", { className: "w-1/3" }, t('NAME')),
                React.createElement("th", { className: "w-1/6" }, t('AMOUNT')),
                React.createElement("th", { className: "w-1/3" },
                    t('GROUP_RELATED_CHARGES'),
                    "?"),
                React.createElement("th", null))));
    };
    const handleCloseValidationAlert = () => {
        setShowValidationAlert(!showValidationAlert);
    };
    const renderInvoicingValidationAlert = () => {
        if (!showValidationAlert) {
            return null;
        }
        if (isValidatingGroupedCharges) {
            return (React.createElement(CargomaticAlert, { type: "info", title: t("AWAITING_CHARGE_VALIDATION"), className: "mb-4", canClose: true, onClose: handleCloseValidationAlert },
                React.createElement(LoadingCircle, null)));
        }
        if (areValidGroupings) {
            return (React.createElement(CargomaticAlert, { type: "success", title: t("CHARGES_VALID_FOR_INVOICING"), className: "mb-4", canClose: true, onClose: handleCloseValidationAlert }, t('ALL_CHARGES_VALID_FOR_INVOICING')));
        }
        if (invalidCharges.length) {
            return (React.createElement(CargomaticAlert, { type: "error", title: t("SOME_CHARGES_INVALID_FOR_INVOICING"), className: "mb-4", canClose: true, onClose: handleCloseValidationAlert }, mapInvoiceValidationAlertBody()));
        }
    };
    const renderAlert = () => {
        return (React.createElement(CargomaticAlert, { type: "info", title: t("SHIPPER_INVOICE_GROUPING_CONFIGURATION"), className: "mb-4" }, t(invoiceGrouping)));
    };
    const mapInvoiceValidationAlertBody = () => (React.createElement(React.Fragment, null,
        React.createElement("div", null, t('SOME_SELECTED_INVOICE_GROUPINGS_INELIGIBLE_TO_INVOICE')),
        React.createElement("ul", { className: "ml-2 mt-2" }, (invalidCharges === null || invalidCharges === void 0 ? void 0 : invalidCharges.length) && invalidCharges.map(err => {
            const { chargeType } = err;
            return (React.createElement("li", { key: `invalid-invoice-type-${chargeType.toLowerCase()}` }, chargeType));
        }))));
    const renderInvoicingErrorsAlert = () => {
        if (!(invoiceErrors === null || invoiceErrors === void 0 ? void 0 : invoiceErrors.length))
            return null;
        return (React.createElement(CargomaticAlert, { type: "error", title: t("INVOICING_RESPONSE"), className: "mb-4", canClose: true }, mapInvoicingAlertBody()));
    };
    const mapInvoicingAlertBody = () => (React.createElement(React.Fragment, null,
        React.createElement("div", null, `${invoiceSuccesses.length} Successful ${invoiceSuccesses.length !== 1 ? 'Invoices' : 'Invoice'}`),
        React.createElement("div", null, `${invoiceErrors.length} ${invoiceErrors.length !== 1 ? 'Invoices' : 'Invoice'} Failed to Generate`),
        React.createElement("ul", null, (invoiceErrors === null || invoiceErrors === void 0 ? void 0 : invoiceErrors.length) && invoiceErrors.map(err => {
            const { chargeTypes } = err;
            const typesWithErrors = chargeTypes === null || chargeTypes === void 0 ? void 0 : chargeTypes.map((type) => type === null || type === void 0 ? void 0 : type.toLowerCase()).join(',');
            return (React.createElement("li", { key: `invoice-error-type-${typesWithErrors}` }, `${t('CHARGE_TYPE')}: ${typesWithErrors}`));
        }))));
    const renderShipment = (shipment) => {
        const shipmentGroup = groupedCharges.find(shipmentGroup => shipmentGroup.shipmentId === shipment._id);
        if (shipmentGroup) {
            return (React.createElement("div", null,
                React.createElement(PanelSection, null,
                    React.createElement("h3", null, shipment === null || shipment === void 0 ? void 0 : shipment.shipment_id)),
                React.createElement(PanelSection, null, renderGroups(shipmentGroup.grouping, shipmentGroup.shipmentId))));
        }
        else {
            return (React.createElement("div", null,
                React.createElement(PanelSection, null,
                    React.createElement("h3", null, shipment === null || shipment === void 0 ? void 0 : shipment.shipment_id))));
        }
    };
    const renderGroups = (groupedCharges, shipmentId) => {
        const groups = Object.keys(groupedCharges);
        return (React.createElement("div", null, groups.map(group => (renderCharges(group, shipmentId, groupedCharges[group])))));
    };
    const renderCharges = (group, shipmentId, groupedCharges) => {
        const groupName = group === 'ALL' ? 'ALL_CHARGES' : group;
        return (React.createElement("div", null,
            React.createElement(CollapsibleItem, { text: t(groupName), defaultOpen: true },
                React.createElement("div", { className: "table-wrapper" },
                    React.createElement("table", { className: "default-table table-fixed mt-4" },
                        ChargeHeaders(shipmentId, group),
                        React.createElement("tbody", null, mapChargeRows(shipmentId, group, groupedCharges)),
                        React.createElement("tfoot", { className: 'bg-white' },
                            React.createElement("tr", null,
                                React.createElement("td", null),
                                React.createElement("td", null,
                                    React.createElement("strong", null, t('TOTAL'))),
                                React.createElement("td", null, getTotal(groupedCharges === null || groupedCharges === void 0 ? void 0 : groupedCharges.charges)),
                                React.createElement("td", null),
                                React.createElement("td", null)))))),
            React.createElement("hr", { className: "my-4" })));
    };
    const getTotal = (groupedCharges) => {
        const total = groupedCharges.reduce((acc, charge) => {
            acc += charge.value;
            return acc;
        }, 0);
        return formatMoney(total);
    };
    const mapChargeRows = (shipmentId, group, shipmentCharges) => {
        const { charges, groupedChargeTypes } = shipmentCharges;
        if (!(groupedChargeTypes === null || groupedChargeTypes === void 0 ? void 0 : groupedChargeTypes.length)) {
            return (charges || []).map((charge) => renderSingleCharge(shipmentId, charge, group, false));
        }
        const rows = [];
        const grouped = groupBy(charges, (charge) => charge === null || charge === void 0 ? void 0 : charge.type);
        Object.keys(grouped).forEach((key) => {
            const chargesByGroupType = grouped[key];
            if (!groupedChargeTypes.includes(key)) {
                chargesByGroupType.forEach((charge) => {
                    rows.push((renderSingleCharge(shipmentId, charge, group, false)));
                });
            }
            else {
                const charge = chargesByGroupType[0];
                const total = chargesByGroupType.reduce((typeTotal, currentCharge) => {
                    typeTotal += currentCharge.value;
                    return typeTotal;
                }, 0);
                const groupedCharge = Object.assign(Object.assign({}, charge), { value: total });
                rows.push((renderSingleCharge(shipmentId, groupedCharge, group, true)));
            }
        });
        return rows;
    };
    const renderSingleCharge = (shipmentId, charge, group, isGrouped) => {
        let chargeIds = [];
        if (isGrouped) {
            chargeIds = getGroupedCharges(shipmentId, group, charge === null || charge === void 0 ? void 0 : charge._id);
        }
        else {
            chargeIds = [charge === null || charge === void 0 ? void 0 : charge._id];
        }
        return (React.createElement("tr", null,
            React.createElement("td", null,
                React.createElement(Checkbox, { checked: charge === null || charge === void 0 ? void 0 : charge.selected, onChange: (checked) => updateSelectedCharges(shipmentId, group, chargeIds, checked) })),
            React.createElement("td", null,
                React.createElement("div", { className: "flex items-center space-x-2" },
                    React.createElement("span", null, t(sluggify(charge === null || charge === void 0 ? void 0 : charge.type))),
                    (charge === null || charge === void 0 ? void 0 : charge.equipment) ? React.createElement(Badge, { className: "green" }, getEquipment(charge === null || charge === void 0 ? void 0 : charge.equipment)) : null)),
            React.createElement("td", null, formatMoney(charge.value)),
            React.createElement("td", null, isGroupEligible(shipmentId, group, charge === null || charge === void 0 ? void 0 : charge.type) && React.createElement(ToggleSwitch, { enabled: isGrouped, onChange: () => groupCharge(shipmentId, group, charge === null || charge === void 0 ? void 0 : charge.type) })),
            React.createElement("td", null)));
    };
    const getEquipment = (equipmentId) => {
        const equipments = uniqBy((shipments || []).reduce((acc, shipment) => {
            const shipmentEquipment = (shipment.stops || []).reduce((equipment, stop) => {
                equipment = equipment.concat(stop.equipments);
                return equipment;
            }, []);
            acc = acc.concat(shipmentEquipment);
            return acc;
        }, []), (eq) => { var _a; return (_a = eq === null || eq === void 0 ? void 0 : eq._id) === null || _a === void 0 ? void 0 : _a.toString(); });
        if (!(equipments && equipments.length)) {
            return null;
        }
        const equipment = equipments.find(equipment => (equipment === null || equipment === void 0 ? void 0 : equipment._id) === equipmentId);
        return equipment === null || equipment === void 0 ? void 0 : equipment.equipment_id;
    };
    const getBody = () => {
        return (React.createElement(LayoutWithSidebar, null,
            React.createElement("div", { className: "bg-gray-50 -mx-4 sm:-mx-6" },
                React.createElement(PanelSection, null,
                    renderAlert(),
                    renderInvoicingValidationAlert(),
                    renderInvoicingErrorsAlert(),
                    React.createElement("div", { className: "flex items-center justify-start mb-2 pr-2 pl-2 py-2" },
                        React.createElement(Label, { labelClassName: "block text-base font-medium text-gray-700 font-semibold pr-3" }, t('GROUP_RELATED_CHARGES')),
                        React.createElement(ToggleSwitch, { enabled: groupAll, onChange: () => setGroupAll(!groupAll) })),
                    React.createElement(Panel, null, shipments.map(shipment => (renderShipment(shipment))))))));
    };
    const getFooter = () => {
        return (React.createElement("div", { className: "flex justify-end" },
            React.createElement(Button, { className: "mr-2", variant: "secondary", onClick: onClose }, t('CANCEL')),
            noDeliveryInvoiceButton(),
            React.createElement(Button, { color: "green", onClick: () => generateInvoices({ noDeliveryInvoice: false }), disabled: isInvoiceButtonDisabled() }, t('GENERATE_INVOICE'))));
    };
    return (React.createElement(CargomaticModal, { title: "Generate Invoice", className: "w-1/2", show: show, onHide: onClose, body: getBody(), footer: getFooter(), size: "large" }));
}
;
const mapDataPayload = (data = []) => {
    return data.map((shipmentData) => {
        var _a;
        const grouping = (_a = Object.keys(shipmentData === null || shipmentData === void 0 ? void 0 : shipmentData.grouping)) === null || _a === void 0 ? void 0 : _a.reduce((data, key) => {
            var _a;
            const selectedCharges = ((shipmentData === null || shipmentData === void 0 ? void 0 : shipmentData.grouping[key].charges) || []).filter((charge) => charge === null || charge === void 0 ? void 0 : charge.selected);
            data[key] = {
                charges: selectedCharges === null || selectedCharges === void 0 ? void 0 : selectedCharges.map((item) => item === null || item === void 0 ? void 0 : item._id.toString()),
                groupedChargeTypes: (_a = shipmentData === null || shipmentData === void 0 ? void 0 : shipmentData.grouping[key]) === null || _a === void 0 ? void 0 : _a.groupedChargeTypes,
            };
            return data;
        }, {});
        return {
            shipmentId: shipmentData === null || shipmentData === void 0 ? void 0 : shipmentData.shipmentId,
            grouping
        };
    });
};
